/**
 * Step 2: Auth Method Selection
 *
 * Allows users to choose their signup method:
 * - Email & Password
 * - Google OAuth (if configured)
 * - Microsoft OAuth (if configured)
 * - Apple OAuth (if configured)
 *
 * For OAuth methods, consent data is saved to sessionStorage before redirect.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 * WP-FE-AUTH-2: Apple OAuth in Signup Wizard
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { useWizard } from './WizardContext';
import AuthMethodCard from './AuthMethodCard';
import { useWizardAnalytics } from '@/hooks/useWizardAnalytics';
import { useAuthStore } from '@/store/authStore';
import { saveOAuthConsent, clearOAuthConsent, type OAuthConsentData } from '@/utils/wizardStorage';
import authApi, { type OAuthProvidersResponse } from '@/services/auth';
import { config } from '@/config';
import { loadAppleAuthSDK, initAppleAuth, signInWithApple } from '@/utils/appleAuth';

/**
 * Email icon
 */
function EmailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

/**
 * Google icon
 */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

/**
 * Microsoft icon
 */
function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Apple icon
 * WP-FE-AUTH-2: Following Apple branding guidelines
 */
function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.24 0-1.44.64-2.2.52-3.06-.4-4.84-5.02-4.12-12.67 1.34-12.96 1.38.07 2.34.8 3.08.8.74 0 2.13-.94 3.6-.8.62.02 2.33.24 3.44 1.84-3.16 1.94-2.64 5.64.5 7.12-.6 1.56-1.4 3.1-2.58 4zm-3.12-15.48c-.94 1.12-2.5 1.98-4.02 1.86-.2-1.5.56-3.08 1.42-4.06.96-1.08 2.58-1.88 3.92-1.92.16 1.58-.46 3.12-1.32 4.12z"/>
    </svg>
  );
}

export interface Step2MethodProps {
  /** Callback when step is complete via email method */
  onNext: () => void;
  /** Callback to go back to previous step */
  onBack: () => void;
}

export default function Step2Method({ onNext, onBack }: Step2MethodProps) {
  const { state, setSelectedMethod, setError, clearError } = useWizard();
  const analytics = useWizardAnalytics();
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithApple } = useAuthStore();

  const [providers, setProviders] = useState<OAuthProvidersResponse | null>(null);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'microsoft' | 'apple' | null>(null);
  const [appleSdkReady, setAppleSdkReady] = useState(false);

  // Fetch OAuth providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await authApi.getOAuthProviders();
        setProviders(response);
      } catch {
        // If we can't fetch providers, assume none are available
        setProviders({ googleEnabled: false, appleEnabled: false, microsoftEnabled: false });
      } finally {
        setProvidersLoading(false);
      }
    };
    fetchProviders();
  }, []);

  // Track step view
  useEffect(() => {
    analytics.trackStepViewed(2, 'Method');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Apple Sign In SDK when provider is enabled
  // WP-FE-AUTH-2: Apple OAuth in Signup Wizard
  useEffect(() => {
    const initApple = async () => {
      // Don't initialize if not configured or not enabled
      if (!config.appleClientId || !providers?.appleEnabled) {
        return;
      }

      try {
        await loadAppleAuthSDK();
        initAppleAuth(config.appleClientId, config.appleRedirectUri, true);
        setAppleSdkReady(true);
      } catch (err) {
        // SDK load failed - Apple Sign In will not be available
        // Log warning but don't surface to user (graceful degradation)
        console.warn('Failed to initialize Apple Sign In SDK:', err instanceof Error ? err.message : 'Unknown error');
      }
    };

    // Only initialize after providers are loaded and Apple is enabled
    if (!providersLoading && providers?.appleEnabled) {
      initApple();
    }
  }, [providersLoading, providers?.appleEnabled]);

  const { selectedMethod, error, consentTimestamp, gdprConsent, marketingConsent } = state;

  // Check if OAuth providers are available
  const isGoogleEnabled = Boolean(config.googleClientId) && providers?.googleEnabled === true;
  const isMicrosoftEnabled = providers?.microsoftEnabled === true;
  // WP-FE-AUTH-2: Requires client ID, backend enabled, AND SDK loaded
  const isAppleEnabled = Boolean(config.appleClientId) && providers?.appleEnabled === true && appleSdkReady;

  /**
   * Handle email method selection
   */
  const handleEmailSelect = useCallback(() => {
    clearError();
    setSelectedMethod('email');
    analytics.trackMethodSelected('email');
    analytics.trackStepCompleted(2, 'Method');
    onNext();
  }, [setSelectedMethod, analytics, onNext, clearError]);

  /**
   * Save consent data and initiate OAuth flow
   */
  const initiateOAuth = useCallback(
    async (provider: 'google' | 'microsoft' | 'apple') => {
      clearError();
      setOauthLoading(provider);

      try {
        // Save consent data to sessionStorage (survives OAuth redirect)
        const consentData: OAuthConsentData = {
          termsAccepted: true,
          privacyAccepted: true,
          gdprConsent: gdprConsent,
          marketingConsent: marketingConsent,
          consentTimestamp: consentTimestamp || new Date().toISOString(),
        };
        saveOAuthConsent(consentData);

        // Track method selection
        analytics.trackMethodSelected(provider);
        setSelectedMethod(provider);

        // WP-AUTH-16: Google OAuth is now handled directly by GoogleLogin component
        // No programmatic click needed - GoogleLogin renders directly and handles popup

        if (provider === 'microsoft') {
          // WP-AUTH-13: Pass consent data as query params for Microsoft OAuth
          // Backend stores this in Redis state and retrieves during callback
          const authUrl = await authApi.getMicrosoftAuthUrl({
            termsAccepted: consentData.termsAccepted,
            privacyAccepted: consentData.privacyAccepted,
            gdprConsent: consentData.gdprConsent,
            marketingConsent: consentData.marketingConsent,
            consentTimestamp: consentData.consentTimestamp,
          });
          window.location.href = authUrl;
          // Note: Don't set loading to false since we're redirecting
        } else if (provider === 'apple') {
          // WP-FE-AUTH-2: Apple OAuth uses popup flow via SDK
          try {
            const response = await signInWithApple();
            const { authorization, user } = response;

            // Apple only provides user info on FIRST authorization
            const firstName = user?.name?.firstName;
            const lastName = user?.name?.lastName;

            // Login with Apple, terms already accepted in Step 1
            await loginWithApple(authorization.id_token, true, undefined, firstName, lastName);

            // Clear saved consent data after successful signup
            clearOAuthConsent();
            analytics.trackStepCompleted(2, 'Method');
            // Navigate to dashboard after successful OAuth signup
            navigate('/dashboard', { replace: true });
          } catch (appleErr) {
            // Handle user cancellation gracefully (don't show error)
            const message = appleErr instanceof Error ? appleErr.message : 'Apple sign-in failed';
            const isCancelled = message.includes('popup_closed') ||
                                message.includes('cancelled') ||
                                message.includes('The operation was canceled') ||
                                message.includes('user_cancelled');
            if (!isCancelled) {
              throw appleErr;
            }
          } finally {
            setOauthLoading(null);
          }
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to initiate OAuth. Please try again.';
        setError(message);
        analytics.trackError('oauth_initiation_failed');
        setOauthLoading(null);
      }
    },
    [
      gdprConsent,
      marketingConsent,
      consentTimestamp,
      analytics,
      setSelectedMethod,
      setError,
      clearError,
      loginWithApple,
      navigate,
    ]
  );

  /**
   * Handle Microsoft OAuth selection
   */
  const handleMicrosoftSelect = useCallback(() => {
    initiateOAuth('microsoft');
  }, [initiateOAuth]);

  /**
   * Handle Apple OAuth selection
   * WP-FE-AUTH-2: Apple OAuth in Signup Wizard
   */
  const handleAppleSelect = useCallback(() => {
    initiateOAuth('apple');
  }, [initiateOAuth]);

  /**
   * Handle Google OAuth success
   * WP-AUTH-16: Called directly when GoogleLogin popup completes successfully
   * Saves consent data and completes the signup flow
   */
  const handleGoogleSuccess = useCallback(
    async (credentialResponse: CredentialResponse) => {
      const idToken = credentialResponse.credential;
      if (!idToken) {
        setError('No credential received from Google');
        setOauthLoading(null);
        return;
      }

      clearError();
      setOauthLoading('google');

      try {
        // WP-AUTH-16: Save consent data (previously done in initiateOAuth)
        const consentData: OAuthConsentData = {
          termsAccepted: true,
          privacyAccepted: true,
          gdprConsent: gdprConsent,
          marketingConsent: marketingConsent,
          consentTimestamp: consentTimestamp || new Date().toISOString(),
        };
        saveOAuthConsent(consentData);

        // Track method selection
        analytics.trackMethodSelected('google');
        setSelectedMethod('google');

        // Login with Google, terms already accepted in Step 1
        await loginWithGoogle(idToken, true);
        // Clear saved consent data after successful signup
        clearOAuthConsent();
        analytics.trackStepCompleted(2, 'Method');
        // Navigate to dashboard after successful OAuth signup
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Google sign-in failed';
        setError(message);
        analytics.trackError('google_oauth_failed');
      } finally {
        setOauthLoading(null);
      }
    },
    [
      loginWithGoogle,
      navigate,
      setError,
      clearError,
      analytics,
      gdprConsent,
      marketingConsent,
      consentTimestamp,
      setSelectedMethod,
    ]
  );

  /**
   * Handle Google OAuth error
   * WP-AUTH-9: Called when Google popup is cancelled or fails
   */
  const handleGoogleError = useCallback(() => {
    setError('Google sign-in was cancelled or failed');
    setOauthLoading(null);
    analytics.trackError('google_oauth_cancelled');
  }, [setError, analytics]);

  /**
   * Handle back navigation
   */
  const handleBack = useCallback(() => {
    analytics.trackStepBack(2, 1);
    onBack();
  }, [analytics, onBack]);

  return (
    <div id="wizard-content" className="space-y-6">
      {/* Step heading */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Choose Sign-Up Method</h2>
        <p className="mt-1 text-sm text-gray-600">
          How would you like to create your FlowIO account?
        </p>
      </div>

      {/* Error display */}
      {error && (
        <Alert variant="error" onDismiss={clearError}>
          {error}
        </Alert>
      )}

      {/* Method options */}
      <div className="space-y-3">
        {/* Email & Password - always available */}
        <AuthMethodCard
          icon={<EmailIcon className="w-5 h-5 text-gray-600" />}
          title="Email & Password"
          description="Sign up with your email; you'll choose a password after confirming it"
          isSelected={selectedMethod === 'email'}
          disabled={!!oauthLoading}
          onClick={handleEmailSelect}
        />

        {/* Google OAuth - if enabled */}
        {/* WP-AUTH-16: Render GoogleLogin directly - hidden button approach doesn't work
            because Google's GSI library uses visibility detection and won't render
            buttons in offscreen containers */}
        {!providersLoading && isGoogleEnabled && (
          <div className="w-full">
            {oauthLoading === 'google' ? (
              /* Show loading state while processing Google OAuth */
              <AuthMethodCard
                icon={<GoogleIcon className="w-5 h-5" />}
                title="Continue with Google"
                description="Signing in..."
                isSelected={true}
                isLoading={true}
                disabled={true}
                onClick={() => {}}
              />
            ) : (
              /* Render actual GoogleLogin button - single click opens popup */
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  width={400}
                  text="continue_with"
                  shape="rectangular"
                />
              </div>
            )}
          </div>
        )}

        {/* Microsoft OAuth - if enabled */}
        {!providersLoading && isMicrosoftEnabled && (
          <AuthMethodCard
            icon={<MicrosoftIcon className="w-5 h-5" />}
            title="Continue with Microsoft"
            description="Sign up using your Microsoft account"
            isSelected={selectedMethod === 'microsoft'}
            isLoading={oauthLoading === 'microsoft'}
            disabled={!!oauthLoading && oauthLoading !== 'microsoft'}
            onClick={handleMicrosoftSelect}
          />
        )}

        {/* Apple OAuth - if enabled */}
        {/* WP-FE-AUTH-2: Apple OAuth in Signup Wizard */}
        {!providersLoading && isAppleEnabled && (
          <AuthMethodCard
            icon={<AppleIcon className="w-5 h-5" />}
            title="Continue with Apple"
            description="Sign up using your Apple ID"
            isSelected={selectedMethod === 'apple'}
            isLoading={oauthLoading === 'apple'}
            disabled={!!oauthLoading && oauthLoading !== 'apple'}
            onClick={handleAppleSelect}
          />
        )}

        {/* Loading state for providers */}
        {providersLoading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin h-5 w-5 border-2 border-gray-300 border-t-primary-600 rounded-full" />
            <p className="mt-2 text-sm text-gray-500">Loading sign-up options...</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="pt-4 flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          disabled={!!oauthLoading}
          className="flex-1"
        >
          Back
        </Button>
      </div>
    </div>
  );
}
